import {useEffect} from 'react';
import {createSelector} from 'reselect';

import {useAppDispatch, useAppSelector} from '../../../../helpers/reduxHelpers';
import {updateEnabledApplicationsAndDisplayBannersAction} from '../../../../store/data/applications/legacyApplicationsActions';
import {getFlatLegacyApplications} from '../../../../store/data/applications/legacyApplicationsSelectors';
import {getFlatIntegrations} from '../../../../store/data/dataSelectors';
import {updateEnabledIntegrationsAndDisplayBannersAction} from '../../../../store/data/integrations/integrationsActions';
import {
  getLocalPrefEnabledIntegrationsIds,
  getLocalPrefEnabledLegacyApplicationIds,
} from '../../../../store/data/localPreferences/localPreferencesSelectors';
import {tryFindAppPanelIdInPanelId, ViewerPanelId} from '../../../../store/panels/panelStateBase';

/*
 * Selectors.
 */

const getFlatIntegrationEnabledIds = createSelector(getFlatIntegrations, (integrations) =>
  integrations.filter((i) => i.isEnabled).map((i) => i.id),
);
const getFlatLegacyApplicationsEnabledIds = createSelector(getFlatLegacyApplications, (applications) =>
  applications.filter((a) => a.isEnabled).map((a) => a.id),
);

/*
 * Hook.
 */

/**
 * This hook tracks the currently applications and integrations to watch for newly enabled
 * integrations and applications. If there is changes to either, we will dispatch an action that will track
 * and potentially display a banner.
 */
export function useNewIntegrationsTrackingHook(panelId: ViewerPanelId) {
  const legacyApplicationIds = useAppSelector(getFlatLegacyApplicationsEnabledIds);
  const integrationIds = useAppSelector(getFlatIntegrationEnabledIds);
  const previouslyEnabledLegacyApplicationIds = useAppSelector(getLocalPrefEnabledLegacyApplicationIds);
  const previouslyEnabledIntegrationIds = useAppSelector(getLocalPrefEnabledIntegrationsIds);
  const appPanelId = tryFindAppPanelIdInPanelId(panelId);

  const dispatch = useAppDispatch();

  // Tracks enabled application changes.
  useEffect(() => {
    // Make sure we have a valid app panel id.
    if (!appPanelId) {
      return;
    }

    // If any application doesn't appear in the previously enabled list, dispatch the action
    // to potentially display banners.
    if (legacyApplicationIds.some((appId) => !previouslyEnabledLegacyApplicationIds.includes(appId))) {
      dispatch(updateEnabledApplicationsAndDisplayBannersAction(appPanelId));
    }
  }, [legacyApplicationIds, previouslyEnabledLegacyApplicationIds, appPanelId, dispatch]);

  // Tracks enabled integration changes.
  useEffect(() => {
    // Make sure we have a valid app panel id.
    if (!appPanelId) {
      return;
    }

    // If any integration doesn't appear in the previously enabled list, dispatch the action
    // to potentially display banners.
    if (integrationIds.some((appId) => !previouslyEnabledIntegrationIds.includes(appId))) {
      dispatch(updateEnabledIntegrationsAndDisplayBannersAction(appPanelId));
    }
  }, [integrationIds, previouslyEnabledIntegrationIds, appPanelId, dispatch]);
}
